import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ROLES = [
  ["customer", "Customer", "Shop spiritual & pooja products"],
  ["business_buyer", "Business Buyer", "Buy wholesale with GST invoicing"],
  ["vendor", "Vendor / Seller", "Sell your products on the marketplace"],
];

export default function Register() {
  const { register } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "customer", phone: "", business_name: "", gstin: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await register(form);
      toast.success("Account created! Welcome to Shri Vrindavan Shop.");
      if (user.role === "vendor") navigate("/vendor/onboarding");
      else navigate("/account");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isBusiness = form.role !== "customer";

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-12">
      <h1 className="text-3xl font-serif font-bold text-stone-900">Create your Shri Vrindavan Shop account</h1>
      <p className="mt-1 text-sm text-stone-500">Join India's spiritual marketplace.</p>

      {error && <div data-testid="register-error" className="mt-4 rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-2.5">{error}</div>}

      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
        <div>
          <span className="text-sm text-stone-600">I am a</span>
          <div className="mt-2 grid sm:grid-cols-3 gap-2">
            {ROLES.map(([val, label, desc]) => (
              <button
                type="button"
                key={val}
                onClick={() => setForm({ ...form, role: val })}
                data-testid={`role-${val}`}
                className={`text-left rounded-lg border p-3 transition-colors ${form.role === val ? "border-secondary bg-secondary/5" : "border-border hover:border-stone-300"}`}
              >
                <div className="font-semibold text-sm text-stone-900">{label}</div>
                <div className="text-[11px] text-stone-500 mt-0.5">{desc}</div>
              </button>
            ))}
          </div>
        </div>

        <label className="block text-sm">
          <span className="text-stone-600">Full Name *</span>
          <input required value={form.name} onChange={set("name")} data-testid="register-name" className="mt-1 w-full rounded-lg border border-border px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30" />
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="text-stone-600">Email *</span>
            <input required type="email" value={form.email} onChange={set("email")} data-testid="register-email" className="mt-1 w-full rounded-lg border border-border px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30" />
          </label>
          <label className="block text-sm">
            <span className="text-stone-600">Phone</span>
            <input value={form.phone} onChange={set("phone")} data-testid="register-phone" className="mt-1 w-full rounded-lg border border-border px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30" />
          </label>
        </div>

        {isBusiness && (
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block text-sm">
              <span className="text-stone-600">Business Name</span>
              <input value={form.business_name} onChange={set("business_name")} data-testid="register-business" className="mt-1 w-full rounded-lg border border-border px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30" />
            </label>
            <label className="block text-sm">
              <span className="text-stone-600">GSTIN</span>
              <input value={form.gstin} onChange={set("gstin")} data-testid="register-gstin" className="mt-1 w-full rounded-lg border border-border px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30" />
            </label>
          </div>
        )}

        <label className="block text-sm">
          <span className="text-stone-600">Password * <span className="text-stone-400">(min 6 characters)</span></span>
          <input required type="password" minLength={8} pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}" title="8+ characters with uppercase, lowercase and a number" value={form.password} onChange={set("password")} data-testid="register-password" className="mt-1 w-full rounded-lg border border-border px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30" />
        </label>

        <button disabled={loading} data-testid="register-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3 hover:bg-[hsl(28_90%_38%)] disabled:opacity-60">
          <UserPlus size={17} /> {loading ? "Creating account..." : "Create Account"}
        </button>
      </form>

      <p className="mt-5 text-center text-sm text-stone-500">
        Already have an account? <Link to="/login" className="font-semibold text-secondary">Sign in</Link>
      </p>
    </div>
  );
}
